/**
 * React context provider for observability.
 *
 * Wraps the application root to provide observability context
 * management (PostHog + Sentry) to all children via hooks.
 *
 * Usage:
 *   <ObservabilityProvider analytics={analyticsClient} errors={errorClient}>
 *     <App />
 *   </ObservabilityProvider>
 */

import { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import type { ObservabilityContext } from './observability-context.types';
import type { IAnalyticsClient } from '../analytics/analytics-client.types';
import type { IErrorClient } from '../errors/error-client.types';
import {
  ObservabilityContextManager,
  type IObservabilityContextManager,
} from './observability-context';

// ── Context types ────────────────────────────────────────────────────

interface ObservabilityProviderValue {
  /** Set full context on login */
  setContext: (ctx: ObservabilityContext) => void;
  /** Update partial context on org/persona switch */
  updateContext: (partial: Partial<ObservabilityContext>) => void;
  /** Clear user context on logout */
  resetContext: () => void;
  /** Get current context snapshot */
  getContext: () => ObservabilityContext | null;
  /** Direct access to analytics client */
  analytics: IAnalyticsClient;
  /** Direct access to error client */
  errors: IErrorClient;
}

const ObservabilityCtx = createContext<ObservabilityProviderValue | null>(null);

// ── Provider props ───────────────────────────────────────────────────

interface ObservabilityProviderProps {
  children: React.ReactNode;
  analytics: IAnalyticsClient;
  errors: IErrorClient;
}

// ── Provider component ───────────────────────────────────────────────

export function ObservabilityProvider({ children, analytics, errors }: ObservabilityProviderProps) {
  const managerRef = useRef<IObservabilityContextManager | null>(null);

  if (!managerRef.current) {
    managerRef.current = new ObservabilityContextManager(analytics, errors);
  }

  const manager = managerRef.current;

  const setContext = useCallback((ctx: ObservabilityContext) => manager.setContext(ctx), [manager]);

  const updateContext = useCallback(
    (partial: Partial<ObservabilityContext>) => manager.updateContext(partial),
    [manager]
  );

  const resetContext = useCallback(() => manager.resetContext(), [manager]);

  const getContext = useCallback(() => manager.getContext(), [manager]);

  const value = useMemo<ObservabilityProviderValue>(
    () => ({
      setContext,
      updateContext,
      resetContext,
      getContext,
      analytics,
      errors,
    }),
    [setContext, updateContext, resetContext, getContext, analytics, errors]
  );

  return <ObservabilityCtx.Provider value={value}>{children}</ObservabilityCtx.Provider>;
}

// ── Hooks ────────────────────────────────────────────────────────────

/**
 * Access the observability context management functions.
 * Must be used within an <ObservabilityProvider>.
 */
export function useObservabilityContext() {
  const ctx = useContext(ObservabilityCtx);
  if (!ctx) {
    throw new Error('useObservabilityContext must be used within an <ObservabilityProvider>');
  }
  return {
    setContext: ctx.setContext,
    updateContext: ctx.updateContext,
    resetContext: ctx.resetContext,
    getContext: ctx.getContext,
  };
}

/**
 * Access the raw analytics and error clients.
 * Prefer useAnalytics() and useErrorCapture() for most use cases.
 */
export function useObservabilityClients() {
  const ctx = useContext(ObservabilityCtx);
  if (!ctx) {
    throw new Error('useObservabilityClients must be used within an <ObservabilityProvider>');
  }
  return {
    analytics: ctx.analytics,
    errors: ctx.errors,
  };
}
